import React, { useCallback, useState } from "react";

import { Accordion, Button, Form, Icon, Input, Modal, TextArea } from "semantic-ui-react";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

import {
  MeLabPdfSection,
  MeLabReportSummary,
  RunSequence,
  getMeLabPdfLock,
} from "react-lib/apps/HISV3/LAB/sequence/LabReport";

// ======================= Constants =======================
const APPROVE_KEY = "ModMeLabPdfPreview_APPROVE";
const SAVE_KEY = "ModMeLabPdfPreview_SAVE";

// ======================= Types =======================
type ModMeLabPdfPreviewProps = {
  open: boolean;
  card: string;
  report: MeLabReportSummary | null;
  patientBirthdate?: string;
  runSequence: RunSequence;
  buttonLoadCheck?: Record<string, any>;
  onClose: () => void;
  onRegenerate: () => void;
};

// ======================= Component =======================
const ModMeLabPdfPreview = (props: ModMeLabPdfPreviewProps) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [sections, setSections] = useState<MeLabPdfSection[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const isApproved = !!props.report?.pdf_approved_at;
  // ไฟล์ใน bucket ถูกเข้ารหัส เจ้าหน้าที่ต้องกรอกรหัสนี้ตอน iframe ถาม
  const pdfLock = getMeLabPdfLock(props.patientBirthdate);

  const handleClose = useCallback(() => {
    setEditing(false);
    setError("");
    props.onClose();
  }, [props.onClose]);

  const handleOpenEditor = useCallback(() => {
    if (!props.report?.report_id) return;

    setLoadingData(true);
    setError("");
    setEditing(true);

    props.runSequence({
      sequence: "LabReport",
      action: "FETCH_MELAB_PDF_DATA",
      reportId: props.report.report_id,
      onSuccess: (ruleSections: MeLabPdfSection[]) => {
        setSections(ruleSections);
        setActiveIndex(0);
        setLoadingData(false);
      },
      onError: (err: any) => {
        setError(typeof err === "string" ? err : "โหลดข้อความไม่สำเร็จ");
        setLoadingData(false);
      },
    });
  }, [props.report?.report_id]);

  const handleChangeSection = useCallback(
    (index: number, key: keyof MeLabPdfSection) => (_e: any, data: any) => {
      setSections((prev) =>
        prev.map((item, idx) => (idx === index ? { ...item, [key]: data.value } : item)),
      );
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!props.report?.report_id) return;

    props.runSequence({
      sequence: "LabReport",
      action: "SAVE_MELAB_PDF_DATA",
      card: props.card,
      buttonLoadKey: SAVE_KEY,
      reportId: props.report.report_id,
      ruleSections: sections,
      birthdate: props.patientBirthdate,
      // กลับหน้า preview ให้ iframe โหลดไฟล์ที่ re-render แล้ว
      onSuccess: () => setEditing(false),
    });
  }, [props.card, props.report?.report_id, props.patientBirthdate, sections]);

  const handleApprove = useCallback(() => {
    if (!props.report?.report_id) return;

    props.runSequence({
      sequence: "LabReport",
      action: "APPROVE_MELAB_PDF",
      card: props.card,
      buttonLoadKey: APPROVE_KEY,
      reportId: props.report.report_id,
      onSuccess: () => {
        setOpenConfirm(false);
        props.onClose();
      },
    });
  }, [props.card, props.report?.report_id]);

  const renderEditor = () => {
    if (loadingData) {
      return <div style={{ textAlign: "center", padding: "2rem" }}>กำลังโหลดข้อความ...</div>;
    }

    if (error) {
      return <div style={{ color: "red", textAlign: "center", padding: "2rem" }}>{error}</div>;
    }

    if (!sections.length) {
      return <div style={{ textAlign: "center", padding: "2rem" }}>ไม่พบข้อความให้แก้ไข</div>;
    }

    return (
      <Accordion fluid styled style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {sections.map((section, index) => (
          <React.Fragment key={`section-${index}`}>
            <Accordion.Title
              active={activeIndex === index}
              index={index}
              onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
            >
              <Icon name="dropdown" />
              {section.section_title || `หัวข้อที่ ${index + 1}`}
            </Accordion.Title>

            <Accordion.Content active={activeIndex === index}>
              <Form>
                <Form.Field>
                  <label>หัวข้อ</label>
                  <Input
                    value={section.section_title || ""}
                    onChange={handleChangeSection(index, "section_title")}
                  />
                </Form.Field>

                <Form.Field>
                  <label>รายละเอียด</label>
                  <TextArea
                    rows={12}
                    value={section.detail_text || ""}
                    onChange={handleChangeSection(index, "detail_text")}
                  />
                </Form.Field>
              </Form>
            </Accordion.Content>
          </React.Fragment>
        ))}
      </Accordion>
    );
  };

  const renderPreview = () => (
    <>
      {!!pdfLock && (
        <div style={{ marginBottom: "0.75rem" }}>
          ไฟล์ถูกล็อก — รหัสเปิดไฟล์คือเดือนและปีเกิดผู้ป่วย <b>{pdfLock}</b>
        </div>
      )}

      {props.report?.pdf_output_url ? (
        <iframe
          title="melab-pdf-preview"
          src={props.report.pdf_output_url}
          style={{ width: "100%", height: "65vh", border: "1px solid #ddd" }}
        />
      ) : (
        <div style={{ textAlign: "center", padding: "2rem" }}>ไม่พบไฟล์ PDF</div>
      )}
    </>
  );

  return (
    <Modal className="centered" open={props.open} onClose={handleClose} size="large" closeIcon>
      <Modal.Header>
        {editing ? "แก้ข้อความ — " : ""}
        {props.report?.report_name || "ผลตรวจ Lab"}
      </Modal.Header>

      <Modal.Content>{editing ? renderEditor() : renderPreview()}</Modal.Content>

      <Modal.Actions>
        {editing ? (
          <>
            <Button
              disabled={props.buttonLoadCheck?.[SAVE_KEY] === "LOADING"}
              onClick={() => setEditing(false)}
            >
              ยกเลิก
            </Button>

            <Button
              color="green"
              disabled={!sections.length}
              loading={props.buttonLoadCheck?.[SAVE_KEY] === "LOADING"}
              onClick={handleSave}
            >
              บันทึกและสร้าง PDF ใหม่
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>ปิด</Button>

            {/* อนุมัติแล้วปิดการแก้ไข เพราะแก้แล้วคนไข้จะเห็นทันทีโดยไม่มีใครตรวจซ้ำ */}
            {!isApproved && (
              <Button color="blue" loading={loadingData} onClick={handleOpenEditor}>
                แก้ข้อความ
              </Button>
            )}

            <Button color="teal" onClick={props.onRegenerate}>
              สร้างใหม่
            </Button>

            {!isApproved && (
              <Button
                color="green"
                disabled={!props.report?.pdf_output_url}
                onClick={() => setOpenConfirm(true)}
              >
                อนุมัติให้คนไข้ดู
              </Button>
            )}
          </>
        )}
      </Modal.Actions>

      <ModConfirm
        openModal={openConfirm}
        centered
        titleName="ยืนยันการอนุมัติ"
        size="mini"
        approveButtonText="อนุมัติ"
        denyButtonText="ยกเลิก"
        content={
          <div style={{ margin: "0.5rem 0" }}>
            อนุมัติ PDF ให้คนไข้ดูใช่หรือไม่ อนุมัติแล้วจะแก้ข้อความไม่ได้อีก
          </div>
        }
        approveButtonLoading={props.buttonLoadCheck?.[APPROVE_KEY] === "LOADING"}
        onApprove={handleApprove}
        onDeny={() => setOpenConfirm(false)}
        onCloseWithDimmerClick={() => setOpenConfirm(false)}
      />
    </Modal>
  );
};

ModMeLabPdfPreview.displayName = "ModMeLabPdfPreview";

export default React.memo(ModMeLabPdfPreview);
